import { Component, OnInit, OnDestroy } from "@angular/core";
import { AuthService } from "../../service/auth.service";
import { Router, ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "ngx-signout-modal",
  template: `
    <div *ngIf="showModal" class="signout-modal-overlay">
      <div class="signout-modal-card">
        <button class="close-btn" (click)="goToLogin()">×</button>

        <h2 class="modal-title">Signed out</h2>

        <p class="modal-text">
          You've been signed out of the dashboard. Please log in again.
        </p>

        <div class="modal-actions">
          <button class="signin-btn" (click)="goToLogin()">
            Sign in again
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .signout-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 99999;
      }

      .signout-modal-card {
        background: #fff;
        width: 100%;
        max-width: 520px;
        padding: 1.2rem;
        border-radius: 12px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.2);
        position: relative;
        text-align: left;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
          Roboto, "Helvetica Neue", sans-serif;
      }

      .modal-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #1b1f23;
      }

      .modal-text {
        font-size: 1rem;
        color: #333;
        margin-bottom: 2rem;
        line-height: 1.5;
      }

      .modal-actions {
        border-top: 1px solid #eaeaea;
        padding-top: 1rem;
        display: flex;
        justify-content: flex-end;
      }

      .signin-btn {
        background-color: #ff9900;
        border: none;
        color: #fff;
        padding: 0.5rem 1.5rem;
        font-size: 1rem;
        font-weight: 600;
        border-radius: 20px;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .signin-btn:hover {
        background-color: #e68a00;
      }

      .close-btn {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: transparent;
        border: none;
        font-size: 1.25rem;
        cursor: pointer;
        color: #555;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
      }

      .close-btn:hover {
        background-color: #f0f0f0;
      }

      /* Mobile responsive */
      @media (max-width: 480px) {
        .signout-modal-card {
          margin: 1rem;
          padding: 1.5rem;
        }

        .modal-title {
          font-size: 1.125rem;
        }

        .modal-text {
          font-size: 0.9rem;
        }
      }
    `,
  ],
})
export class SignoutModalComponent implements OnInit, OnDestroy {
  showModal = false;
  private destroy$ = new Subject<void>();
  private isInitialLoad = true;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 1000);

    this.authService
      .getSessionExpired()
      .pipe(takeUntil(this.destroy$))
      .subscribe((expired) => {
        if (expired && this.shouldShowModal()) {
          console.log("Session expired - showing modal");
          this.showModal = true;
        }
      });

    this.authService
      .getUserSignedOut()
      .pipe(takeUntil(this.destroy$))
      .subscribe((signedOut) => {
        if (signedOut && this.shouldShowModal()) {
          console.log("User signed out - showing modal");
          this.showModal = true;
        }
      });

    // Backup: Check if redirected due to session expiration via query params
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params["reason"] === "session_expired" && this.shouldShowModal()) {
          console.log("Session expired via query param - showing modal");
          this.showModal = true;
        }
      });

    // Backup: Check if user becomes unauthenticated (fallback)
    this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      const isAuthenticated = !!user && this.authService.loggedIn();

      if (
        !isAuthenticated &&
        this.shouldShowModal() &&
        !this.isInitialLoad &&
        !this.showModal
      ) {
        this.showModal = true;
      }
    });
  }

  private shouldShowModal(): boolean {
    const currentUrl = this.router.url;
    const isOnAuthPage = currentUrl.includes("/auth");
    const isOnRootPage = currentUrl === "/";

    if (isOnAuthPage || isOnRootPage || this.isInitialLoad) {
      return false;
    }

    if (this.showModal) {
      return false;
    }

    return true;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToLogin() {
    this.showModal = false;
    this.authService.clearSessionFlags();
    this.router.navigate(["/auth"]);
  }
}
